var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Input } from '../Input';
import { InputMessage } from '../../atoms/InputMessage';
import { DateInputSplitStyles } from './DateInputSplitStyle';
import { LabelSmall } from '../../labels';
import { randomString } from '../../utils/randomString';
dayjs.extend(customParseFormat);
// can be passed a different object with different rules
var validationRules = {
    day: {
        max: 31,
        min: 1,
    },
    month: {
        max: 12,
        min: 1,
    },
    year: {
        max: dayjs().subtract(18, 'year').year(),
        min: 1910,
    },
};
export var DateInputSplit = function (_a) {
    var _b = _a.dateStructure, dateStructure = _b === void 0 ? ['day', 'month', 'year'] : _b, // Controls which element is being displayed
    _c = _a.errorMessage, // Controls which element is being displayed
    errorMessage = _c === void 0 ? "Sorry, this doesn't look quite right. Please double check and try again" : _c, // This is the default message, but can be changed by passing this prop
    customValidation = _a.customValidation, // We can pass custom validation. It must follow the above structure (validationRules)
    onChange = _a.onChange, // onChange function can be passed to do things with the values, such as saving them in the app's state
    onBlur = _a.onBlur, // onBlur function can be passed to do things with the values, such as saving them in the app's state
    onError = _a.onError, // a function to handle the errors at the page level
    _d = _a.showLabels, // a function to handle the errors at the page level
    showLabels = _d === void 0 ? true : _d, // in case we want to use the molecule without the hovering labels
    _e = _a.validateOnBlur, // in case we want to use the molecule without the hovering labels
    validateOnBlur = _e === void 0 ? false : _e, defaultValue = _a.defaultValue, _f = _a.toggleErrorMessage, toggleErrorMessage = _f === void 0 ? false : _f, _g = _a.dataCy, dataCy = _g === void 0 ? 'dob' : _g, _h = _a.tabFocusTrap, tabFocusTrap = _h === void 0 ? 0 : _h, ariaLiveOnFocus = _a.ariaLiveOnFocus;
    var _j = useState({}), date = _j[0], setDate = _j[1];
    var _k = useState({}), errors = _k[0], setErrors = _k[1];
    var _l = useState({}), valid = _l[0], setValid = _l[1];
    var _m = useState(false), focused = _m[0], setFocused = _m[1];
    var _o = useState(false), showErrorMessage = _o[0], setShowErrorMessage = _o[1];
    var validation = customValidation || validationRules;
    var errorIdRef = useRef(randomString());
    var dateInputIdArrayRef = useRef([randomString(), randomString(), randomString()]);
    var validateField = function (value, field) {
        return (value && value <= validation[field].max && value >= validation[field].min) || false;
    };
    useEffect(function () {
        setDate({
            day: (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.day) || undefined,
            month: (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.month) || undefined,
            year: (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.year) || undefined,
        });
    }, [defaultValue]);
    useEffect(function () {
        if (dateStructure.length === 3) {
            if (date.day !== undefined && date.month !== undefined && date.year !== undefined) {
                dayjsValidateDate();
            }
        }
        setValid({
            day: validateField(date.day, 'day'),
            month: validateField(date.month, 'month'),
            year: validateField(date.year, 'year'),
        });
        setErrors({
            day: date.day !== undefined && !validateField(date.day, 'day'),
            month: date.month !== undefined && !validateField(date.month, 'month'),
            year: date.year !== undefined && !validateField(date.year, 'year'),
        });
    }, [date]);
    useEffect(function () {
        if (onError) {
            onError(errors);
        }
    }, [errors]);
    var dayjsValidateDate = function () {
        var _a, _b, _c, _d;
        var day = ((_a = Number(date === null || date === void 0 ? void 0 : date.day)) === null || _a === void 0 ? void 0 : _a.toString().length) === 1 && Number(date.day) > 0
            ? "0".concat((_b = Number(date === null || date === void 0 ? void 0 : date.day)) === null || _b === void 0 ? void 0 : _b.toString())
            : date.day;
        var month = ((_c = Number(date === null || date === void 0 ? void 0 : date.month)) === null || _c === void 0 ? void 0 : _c.toString().length) === 1 ? "0".concat((_d = Number(date === null || date === void 0 ? void 0 : date.month)) === null || _d === void 0 ? void 0 : _d.toString()) : date === null || date === void 0 ? void 0 : date.month;
        var fullDate = (dateStructure === null || dateStructure === void 0 ? void 0 : dateStructure.length) === 2 ? "".concat(day, "/").concat(month) : "".concat(day, "/").concat(month, "/").concat(date.year);
        if (!dayjs(fullDate, 'DD/MM/YYYY', true).isValid()) {
            setShowErrorMessage(true);
        }
        else {
            setShowErrorMessage(false);
        }
    };
    var handleChange = function (value, field) {
        var _a;
        setDate(__assign(__assign({}, date), (_a = {}, _a[field] = value, _a)));
        if (onChange)
            onChange(value, field);
    };
    var handleBlur = function (value, field) {
        var _a;
        if (validateOnBlur) {
            setDate(__assign(__assign({}, date), (_a = {}, _a[field] = value, _a)));
        }
        if (onBlur)
            onBlur(value, field);
    };
    return (_jsx(DateInputSplitStyles, { children: _jsxs("div", __assign({ className: "split-picker-wrapper" }, { children: [_jsxs("div", __assign({ className: "split-picker-component" }, { children: [(dateStructure === null || dateStructure === void 0 ? void 0 : dateStructure.includes('day')) && (_jsxs("div", __assign({ className: "split-picker-item" }, { children: [showLabels && (_jsx(LabelSmall, __assign({ id: dateInputIdArrayRef.current[0], className: "split-label" }, { children: "Day" }))), _jsx(Input, { name: "day", onChange: !validateOnBlur ? function (value) { return handleChange(value, 'day'); } : function () { return false; }, onBlur: function (value) {
                                        setFocused(false);
                                        if (validateOnBlur || onBlur) {
                                            handleBlur(value, 'day');
                                        }
                                    }, onFocus: function () { return setFocused(true); }, className: "date-picker-item day-picker", type: "number", placeHolder: "DD", value: date === null || date === void 0 ? void 0 : date.day, errorNoMessage: errors.day || toggleErrorMessage, valid: valid.day && !errors.day, keepLabel: false, dataCy: "".concat(dataCy, "-day"), customValidator: function (value) { return value.toString().length <= 2; }, tabFocusTrap: tabFocusTrap, ariaLabelledBy: dateInputIdArrayRef.current[0], ariaErrorMessage: errorIdRef.current })] }))), (dateStructure === null || dateStructure === void 0 ? void 0 : dateStructure.includes('month')) && (_jsxs("div", __assign({ className: "split-picker-item" }, { children: [showLabels && (_jsx(LabelSmall, __assign({ id: dateInputIdArrayRef.current[1], className: "split-label" }, { children: "Month" }))), _jsx(Input, { name: "month", onChange: !validateOnBlur ? function (value) { return handleChange(value, 'month'); } : function () { return false; }, onBlur: function (value) {
                                        setFocused(false);
                                        if (validateOnBlur || onBlur) {
                                            handleBlur(value, 'month');
                                        }
                                    }, onFocus: function () { return setFocused(true); }, className: "date-picker-item month-picker", type: "number", placeHolder: "MM", value: date === null || date === void 0 ? void 0 : date.month, errorNoMessage: errors.month || toggleErrorMessage, valid: valid.month && !errors.month, keepLabel: false, dataCy: "".concat(dataCy, "-month"), customValidator: function (value) { return value.toString().length <= 2; }, tabFocusTrap: tabFocusTrap, ariaLabelledBy: dateInputIdArrayRef.current[1], ariaErrorMessage: errorIdRef.current })] }))), (dateStructure === null || dateStructure === void 0 ? void 0 : dateStructure.includes('year')) && (_jsxs("div", __assign({ className: "split-picker-item" }, { children: [showLabels && (_jsx(LabelSmall, __assign({ id: dateInputIdArrayRef.current[2], className: "split-label" }, { children: "Year" }))), _jsx(Input, { name: "year", onChange: !validateOnBlur ? function (value) { return handleChange(value, 'year'); } : function () { return false; }, onBlur: function (value) {
                                        setFocused(false);
                                        if (validateOnBlur || onBlur) {
                                            handleBlur(value, 'year');
                                        }
                                    }, onFocus: function () { return setFocused(true); }, className: "date-picker-item year-picker", type: "number", placeHolder: "YYYY", value: date === null || date === void 0 ? void 0 : date.year, errorNoMessage: errors.year || toggleErrorMessage, valid: valid.year && !errors.year, keepLabel: false, dataCy: "".concat(dataCy, "-year"), customValidator: function (value) { return value.toString().length <= 4; }, tabFocusTrap: tabFocusTrap, ariaLabelledBy: dateInputIdArrayRef.current[2], ariaErrorMessage: errorIdRef.current })] })))] })), (showErrorMessage || toggleErrorMessage) && (_jsx(InputMessage, { errorId: errorIdRef.current, className: "split-date-error", message: errorMessage, ariaLive: ariaLiveOnFocus ? focused : true }))] })) }));
};
